<template>
    <section id="user">
        <div class="page-breadcrumbs">
            <page-breadcrumbs :list="breadcrumbs" />
        </div>
        <h2 class="page-title">
            Users
        </h2>
        <el-row
            :gutter="15"
            justify="space-between"
        >
            <el-col
                class="col-auto"
            >
                <user-filter
                    v-model="filters"
                />
            </el-col>
            <el-col
                v-if="$auth.check('users.create')"
                class="col-auto"
            >
                <el-button
                    type="primary"
                    icon="plus"
                    @click="$router.push({name: 'users.add'})"
                >
                    Add
                </el-button>
            </el-col>
        </el-row>

        <!-- table -->
        <el-table
            v-loading="usersLoading"
            :data="users"
            highlight-current-row
            class="w-100"
            @sort-change="handleSortChange"
            @filter-change="handleFilterChange"
        >
            <el-table-column
                prop="name"
                label="Name"
                min-width="200"
            />
            <el-table-column
                prop="email"
                label="Email"
                min-width="200"
            />
            <el-table-column
                prop="role"
                label="Role"
                min-width="130"
            />
            <el-table-column
                :sort-orders="sortOrders"
                prop="updated_at"
                label="Updated"
                width="130"
            >
                <template #default="scope">
                    {{ $formatDate(scope.row.updated_at) }}
                </template>
            </el-table-column>
            <el-table-column
                label="Actions"
                min-width="180"
                align="right"
            >
                <template #default="scope">
                    <el-button
                        v-if="$auth.check('users.edit')"
                        size="small"
                        icon="edit"
                        @click="$router.push({name: 'users.edit', params: {id: scope.row._id}})"
                    >
                        Edit
                    </el-button>
                    <el-button
                        v-if="$auth.check('users.delete')"
                        type="danger"
                        size="small"
                        icon="delete"
                        @click="handleDelete(scope.row)"
                    >
                        Delete
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- pagination -->
        <el-pagination
            v-model:current-page="page"
            :page-size="per_page"
            :total="total"
            hide-on-single-page
            layout="prev, pager, next, ->, total"
            class="el-pagination--align-right"
            @current-change="fetchData"
            @size-change="fetchData"
        />
    </section>
</template>

<script setup>
import {ref, onMounted, watch} from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import userApi from '../userApi'
import UserFilter from './UserFilter'
import PageBreadcrumbs from '~/base/components/page-breadcrumbs/PageBreadcrumbs'

const usersLoading = ref(false)
const users = ref(null)
const total = ref(0)
const sortBy = ref('id,asc')
const sortOrders = ref(['ascending', 'descending'])
const filters = ref({search: ''})
const page = ref(1)
const per_page = ref(10)
const breadcrumbs = ref([
    {
        title: '..',
        path: '/'
    },
    {
        title: 'Users',
    }
])

onMounted(() => fetchData())

watch(
    () => filters.value,
    val => applySearch(),
    { deep: true }
)

function handleSortChange(val) {
    if (val.prop != null && val.order != null) {
        let sort = val.order.startsWith('a') ? 'asc' : 'desc'
        sortBy.value = val.prop + ',' + sort
        fetchData()
    }
}
function handleFilterChange() {
    fetchData()
}

async function fetchData() {
    usersLoading.value = true
    let params = {
        page: page.value,
        sortBy: sortBy.value,
        ...filters.value
    }
    const {data} = await userApi.all(params)
    users.value = data.data
    total.value = data.total
    per_page.value = data.per_page
    usersLoading.value = false
}

function handleDelete(row) {
    ElMessageBox.confirm('This will permanently delete the user. Continue?', 'Warning', {
        type: 'warning'
    }).then(() => {
        userApi.destroy(row._id).then((response) => {
            ElMessage.success( 'Deleted successfully')
            fetchData()
        })
    })
}

const applySearch = _.debounce( function() {
    fetchData()
}, 300)
</script>
